/**
 * 路由工具函数
 */

/**
 * 安全的动态导入函数，带有错误处理
 * @param {Function} importFn - 动态导入函数
 * @param {string} componentName - 组件名称（用于错误日志）
 * @returns {Promise} 返回组件或错误组件
 */
export function safeImport(importFn, componentName = 'Unknown') {
  return importFn().catch(error => {
    console.error(`Failed to load component ${componentName}:`, error);
    
    // 尝试加载通用错误组件
    return import('@/views/error/ComponentLoadError.vue').catch(fallbackError => {
      console.error('Failed to load fallback error component:', fallbackError);
      
      // 如果连错误组件都加载失败，返回一个简单的内联组件
      return {
        template: `
          <div class="inline-error-container" style="
            padding: 40px 20px;
            text-align: center;
            min-height: 300px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: #f5f7fa;
            border-radius: 8px;
            margin: 20px;
          ">
            <div style="
              font-size: 48px;
              color: #f56c6c;
              margin-bottom: 16px;
            ">⚠️</div>
            <h2 style="
              color: #303133;
              margin: 0 0 12px 0;
              font-size: 20px;
            ">组件加载失败</h2>
            <p style="
              color: #606266;
              margin: 0 0 20px 0;
              line-height: 1.5;
            ">${componentName} 页面暂时无法访问，请刷新页面重试。</p>
            <div style="display: flex; gap: 12px; flex-wrap: wrap; justify-content: center;">
              <button 
                onclick="window.location.reload()" 
                style="
                  background: #409eff;
                  color: white;
                  border: none;
                  padding: 8px 16px;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 14px;
                "
              >刷新页面</button>
              <button 
                onclick="history.back()" 
                style="
                  background: #909399;
                  color: white;
                  border: none;
                  padding: 8px 16px;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 14px;
                "
              >返回上一页</button>
            </div>
          </div>
        `
      };
    });
  });
}

/**
 * 创建带有错误处理的路由组件
 * @param {string} path - 组件路径
 * @param {string} componentName - 组件名称
 * @returns {Function} 路由组件加载函数
 */
export function createRouteComponent(path, componentName) {
  // 直接返回异步加载函数，Vue Router需要的格式
  const loadComponent = () => {
    console.log(`开始加载组件: ${componentName}, 路径: ${path}`);
    
    // 根据环境使用不同的导入方式
    const dynamicImport = () => {
      // 开发环境和生产环境的路径处理
      if (import.meta.env.DEV) {
        // 开发环境：直接使用相对路径
        switch(path) {
          case '@/views/Login.vue':
            return import('../views/Login.vue');
          case '@/views/Register.vue':
            return import('../views/Register.vue');
          case '@/views/ForgotPassword.vue':
            return import('../views/ForgotPassword.vue');
          case '@/views/ResetPassword.vue':
            return import('../views/ResetPassword.vue');
          default:
            // 尝试动态导入
            return import(/* @vite-ignore */ path);
        }
      } else {
        // 生产环境：使用vite的动态导入
        return import(/* @vite-ignore */ path);
      }
    };

    return safeImport(dynamicImport, componentName)
      .then(component => {
        console.log(`组件加载成功: ${componentName}`);
        return component;
      })
      .catch(error => {
        console.error(`组件加载失败: ${componentName}`, error);
        // 如果是生产环境，尝试重新加载页面
        if (import.meta.env.PROD && error.message.includes('Failed to fetch')) {
          console.log('检测到生产环境下的加载失败，尝试刷新页面...');
          setTimeout(() => {
            if (confirm(`组件 ${componentName} 加载失败，是否刷新页面重试？`)) {
              window.location.reload();
            }
          }, 1000);
        }
        throw error;
      });
  };

  // 为函数添加displayName避免Vue Router警告
  loadComponent.displayName = `${componentName}Loader`;
  
  return loadComponent;
}

/**
 * 预加载关键组件
 * @param {Array} componentPaths - 组件路径数组
 */
export function preloadComponents(componentPaths) {
  if ('requestIdleCallback' in window) {
    // 在浏览器空闲时预加载
    requestIdleCallback(() => {
      componentPaths.forEach(path => {
        import(/* @vite-ignore */ path).catch(error => {
          console.warn(`Failed to preload component ${path}:`, error);
        });
      });
    });
  } else {
    // 降级方案：延迟预加载
    setTimeout(() => {
      componentPaths.forEach(path => {
        import(/* @vite-ignore */ path).catch(error => {
          console.warn(`Failed to preload component ${path}:`, error);
        });
      });
    }, 2000);
  }
}

/**
 * 路由错误处理中间件
 * 处理动态导入失败等路由错误，提供自动重试机制
 */
export function setupRouterErrorHandling(router) {
  // 记录重试次数，避免无限刷新
  const RETRY_KEY = '__router_import_retry__';
  const MAX_RETRIES = 2;
  
  router.onError((error) => {
    console.error('Router error:', error);
    
    // 如果是动态导入错误
    if (error.message && error.message.includes('Failed to fetch dynamically imported module')) {
      console.error('Dynamic import failed, attempting to reload...');
      
      // 获取当前重试次数
      const retryCount = parseInt(sessionStorage.getItem(RETRY_KEY) || '0', 10);
      
      if (retryCount < MAX_RETRIES) {
        // 增加重试次数
        sessionStorage.setItem(RETRY_KEY, String(retryCount + 1));
        console.log(`Auto-reloading page (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        
        // 自动刷新页面，不需要用户确认
        // 使用短延迟确保日志被记录
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        // 重试次数用尽，清除计数并显示友好提示
        sessionStorage.removeItem(RETRY_KEY);
        console.error('Max retries reached, showing error message');
        
        // 显示更友好的错误提示（使用 Element Plus 如果可用）
        if (typeof window !== 'undefined') {
          // 尝试使用 Element Plus 的消息提示
          try {
            import('element-plus').then(({ ElMessage }) => {
              ElMessage.error({
                message: '页面加载失败，请清除浏览器缓存后刷新重试',
                duration: 5000,
                showClose: true
              });
            }).catch(() => {
              // 如果 Element Plus 也加载失败，使用原生 alert
              alert('页面加载失败，请清除浏览器缓存后刷新重试（快捷键: Ctrl+Shift+R）');
            });
          } catch {
            alert('页面加载失败，请清除浏览器缓存后刷新重试（快捷键: Ctrl+Shift+R）');
          }
        }
      }
    }
  });
  
  // 导航成功后清除重试计数
  router.afterEach(() => {
    sessionStorage.removeItem(RETRY_KEY);
  });
}
